/**
 * Copyright 2019, OpenCensus Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import * as webCore from '@opencensus/web-core';
/**
 * This is a recent time in epoch milliseconds. Timestamps before this are
 * assumed to be in browser performance clock millseconds, and timestamps after
 * it are assumed to be in epoch milliseconds.
 */
var RECENT_EPOCH_MS = 1500000000000; // July 13, 2017.
/**
 * Converts a RootSpan type from @opencensus/web-core to the Span JSON structure
 * expected by the OpenCensus Agent's HTTP/JSON (grpc-gateway) API.
 */
export function adaptRootSpan(rootSpan) {
    var adaptedSpans = rootSpan.spans.map(adaptSpan);
    adaptedSpans.unshift(adaptSpan(rootSpan));
    return adaptedSpans;
}
function adaptString(value) {
    return { value: value };
}
/** Converts hexadecimal string to base64 string. */
function hexToBase64(hexStr) {
    var hexStrLen = hexStr.length;
    var hexAsciiCharsStr = '';
    for (var i = 0; i < hexStrLen; i += 2) {
        var hexPair = hexStr.substring(i, i + 2);
        // tslint:disable-next-line:ban Needed to parse hexadecimal.
        var hexVal = parseInt(hexPair, 16);
        hexAsciiCharsStr += String.fromCharCode(hexVal);
    }
    return btoa(hexAsciiCharsStr);
}
function adaptTraceState(coreTraceState) {
    if (!coreTraceState || !coreTraceState.length)
        return {};
    var entries = coreTraceState.split(',');
    var apiTraceState = {};
    for (var _i = 0, entries_1 = entries; _i < entries_1.length; _i++) {
        var entry = entries_1[_i];
        var _a = entry.split('='), key = _a[0], value = _a[1];
        apiTraceState[key] = value;
    }
    return apiTraceState;
}
function adaptValue(value) {
    var valType = typeof value;
    if (valType === 'boolean') {
        return { boolValue: value };
    }
    if (valType === 'number') {
        return { doubleValue: value };
    }
    return { stringValue: adaptString(String(value)) };
}
function adaptAttributes(attributes) {
    var attributeMap = {};
    for (var _i = 0, _a = Object.keys(attributes); _i < _a.length; _i++) {
        var key = _a[_i];
        attributeMap[key] = adaptValue(attributes[key]);
    }
    return { attributeMap: attributeMap };
}
function adaptAnnotation(annotation) {
    return {
        time: adaptTimestampNumber(annotation.timestamp),
        annotation: {
            description: adaptString(annotation.description),
            attributes: adaptAttributes(annotation.attributes),
        },
    };
}
/**
 * Adapts a timestamp number for an annotation or message event. For
 * opencensus-web, those timestamps are allowed to be either in epoch
 * milliseconds or in browser performance clock milliseconds. This determines
 * which one it likely is based on the size of the number.
 * @return ISO string for timestamp
 */
function adaptTimestampNumber(timestamp) {
    if (timestamp > RECENT_EPOCH_MS) {
        return new Date(timestamp).toISOString();
    }
    return webCore.getIsoDateStrForPerfTime(timestamp);
}
function adaptMessageEvent(messageEvent) {
    return {
        time: adaptTimestampNumber(messageEvent.timestamp),
        messageEvent: {
            // tslint:disable-next-line:ban Needed to parse hexadecimal.
            id: messageEvent.id,
            type: messageEvent.type,
            uncompressedSize: messageEvent.uncompressedSize,
            compressedSize: messageEvent.compressedSize,
        },
    };
}
function adaptTimeEvents(annotations, messageEvents) {
    return {
        timeEvent: annotations
            .map(adaptAnnotation)
            .concat(messageEvents.map(adaptMessageEvent)),
    };
}
function adaptLink(link) {
    return {
        traceId: hexToBase64(link.traceId),
        spanId: hexToBase64(link.spanId),
        type: link.type,
        attributes: adaptAttributes(link.attributes),
    };
}
function adaptLinks(links) {
    return { link: links.map(adaptLink) };
}
/**
 * Returns an ISO date string for a span high-resolution browser performance
 * clock time when available (if the span was a `webCore.Span` instance) or
 * otherwise using the more general `@opencensus/core` Date-typed times.
 */
function adaptSpanTime(perfTime, fallbackTime) {
    return perfTime === undefined
        ? fallbackTime.toISOString()
        : webCore.getIsoDateStrForPerfTime(perfTime);
}
function adaptSpan(span) {
    // The stackTrace and childSpanCount attributes are not currently supported by
    // opencensus-web.
    return {
        traceId: hexToBase64(span.traceId),
        spanId: hexToBase64(span.id),
        tracestate: adaptTraceState(span.traceState),
        parentSpanId: hexToBase64(span.parentSpanId),
        name: adaptString(span.name),
        kind: span.kind,
        startTime: adaptSpanTime(span.startPerfTime, span.startTime),
        endTime: adaptSpanTime(span.endPerfTime, span.endTime),
        attributes: adaptAttributes(span.attributes),
        timeEvents: adaptTimeEvents(span.annotations, span.messageEvents),
        links: adaptLinks(span.links),
        status: span.status,
        sameProcessAsParentSpan: !span.remoteParent,
    };
}
//# sourceMappingURL=adapters.js.map